// A dataset's copy as a query: one cache entry per dataset, shared by every
// UI on the screen that reads it, caught up by the realtime notices.
//
// Promise:
// - The entry is never stale by time (`staleTime: Infinity`): it is fetched
//   when first wanted, and again only when a notice says the dataset moved
//   past the copy (`dataset.advanced` with a higher seq) or changed its meta
//   (`dataset.changed`). A notice nobody holds a copy for fetches nothing.
// - Every UI reading the same dataset shares the entry, so they cost one sync.
// - Not kept in the persisted query cache: the rows have their own copy
//   (copies.ts).
//
// The person's own datasets are listed here too (their screen's list).
//
// The write tokens of a dataset (its screen) are here too: listed only for
// those who may write it; a new token is in the issue's answer alone, never
// in the cache.
import {
  infiniteQueryOptions,
  type QueryClient,
  queryOptions,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query";
import type {
  DatasetDTO,
  DatasetQueryAnswer,
  DatasetRow,
  DatasetTokenDTO,
  DatasetWriteOp,
  DatasetWriteResult,
} from "@trameria/core/datasets-shared";
import { useApi } from "../app/context";
import { type Api, ApiError, idempotencyKeyFor } from "../data/api";
import { optimisticMutation } from "../data/optimistic";
import type { RealtimeHandlers } from "../realtime/realtime";
import type { DatasetCopies } from "./copies";
import { type DatasetCopy, syncDataset } from "./sync";

const enc = encodeURIComponent;

export const datasetSyncKey = (id: string) =>
  ["me", "datasets", id, "sync"] as const;

/** The person's own datasets (`/api/me/datasets`), the latest change first. */
export const meDatasetListKey = ["me", "datasets", "own"] as const;

export const meDatasetListQuery = (api: Api) =>
  infiniteQueryOptions({
    queryKey: meDatasetListKey,
    queryFn: ({ pageParam }) =>
      api<{ datasets: DatasetDTO[]; nextCursor: string | null }>(
        `/api/me/datasets${pageParam ? `?before=${enc(pageParam)}` : ""}`,
      ),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.nextCursor,
  });

/** Makes a personal dataset. */
export function useCreateMeDataset() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { title: string }) =>
      (
        await api<{ dataset: DatasetDTO }>("/api/me/datasets", {
          method: "POST",
          body: input,
          idempotencyKey: idempotencyKeyFor(input),
        })
      ).dataset,
    onSettled: () => qc.invalidateQueries({ queryKey: meDatasetListKey }),
  });
}

export const datasetTokensKey = (id: string) =>
  ["me", "datasets", id, "tokens"] as const;

export const datasetTokensQuery = (api: Api, id: string) =>
  queryOptions({
    queryKey: datasetTokensKey(id),
    queryFn: async () =>
      (
        await api<{ tokens: DatasetTokenDTO[] }>(
          `/api/datasets/${enc(id)}/tokens`,
        )
      ).tokens,
  });

/** Issues a token: the answer (with the token) is the mutation's data only. */
export function useIssueDatasetToken(id: string) {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { name: string }) =>
      api<DatasetTokenDTO & { token: string }>(
        `/api/datasets/${enc(id)}/tokens`,
        {
          method: "POST",
          body: input,
          idempotencyKey: idempotencyKeyFor(input),
        },
      ),
    onSettled: () =>
      qc.invalidateQueries({ queryKey: datasetTokensKey(id), exact: true }),
  });
}

export function useRevokeDatasetToken(id: string) {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (tokenId: string) =>
      api(`/api/datasets/${enc(id)}/tokens/${enc(tokenId)}`, {
        method: "DELETE",
      }),
    ...optimisticMutation<DatasetTokenDTO[], string>(
      qc,
      datasetTokensKey(id),
      (old, tokenId) => old?.filter((t) => t.id !== tokenId),
    ),
  });
}

/**
 * Writes rows by hand (the screen's form). The rows show through the copy's
 * sync afterwards (their `_id` and `_at` are the server's).
 */
export function useWriteDatasetRows(id: string) {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { ops: DatasetWriteOp[] }) =>
      api<DatasetWriteResult>(`/api/datasets/${enc(id)}/rows`, {
        method: "POST",
        body: input,
        idempotencyKey: idempotencyKeyFor(input),
      }),
    onSuccess: (written) => catchUpDataset(qc, id, written.seq),
  });
}

export interface DatasetQueryDeps {
  api: Api;
  copies: DatasetCopies;
  qc: QueryClient;
  fetchSnapshot?(url: string): Promise<DatasetRow[]>;
}

export function datasetSyncQuery(deps: DatasetQueryDeps, id: string) {
  const queryKey = datasetSyncKey(id);
  return queryOptions<DatasetCopy, Error>({
    queryKey,
    queryFn: () =>
      syncDataset(
        deps,
        id,
        deps.qc.getQueryData<DatasetCopy>(queryKey),
        // The kept copy shows while the sync is on its way.
        (local) => {
          if (!deps.qc.getQueryData(queryKey))
            deps.qc.setQueryData(queryKey, local);
        },
      ),
    staleTime: Number.POSITIVE_INFINITY,
    // The rows are many and never changed in place: compare by reference.
    structuralSharing: false,
    // A refusal (404: gone or not seen) is final; a network failure tries twice more.
    retry: (count, error) =>
      !(error instanceof ApiError && error.status < 500) && count < 2,
  });
}

/** A query answered where the dataset lives (a Vault's screens). */
export const datasetQueryKey = (id: string, query: string) =>
  ["me", "datasets", id, "query", query] as const;

/**
 * The result of one query, worked out where the dataset is (`query` is its
 * JSON). Fetched again only on the dataset's notices, as the copy is.
 */
export function datasetQueryQuery(api: Api, id: string, query: string) {
  return queryOptions<DatasetQueryAnswer, Error>({
    queryKey: datasetQueryKey(id, query),
    queryFn: () =>
      api<DatasetQueryAnswer>(`/api/datasets/${enc(id)}/query`, {
        method: "POST",
        body: { query: JSON.parse(query) as unknown },
      }),
    staleTime: Number.POSITIVE_INFINITY,
    retry: (count, error) =>
      !(error instanceof ApiError && error.status < 500) && count < 2,
  });
}

/**
 * Brings the copy the screen holds up to `seq` (a write's answer, a notice):
 * nothing when it is there already; a sync on its way is waited for first
 * and asked again only if it stopped short. A write and its own notice so
 * cost one sync, whichever comes first. A copy nobody holds is left alone.
 */
export async function catchUpDataset(
  qc: QueryClient,
  id: string,
  seq: number,
): Promise<void> {
  const queryKey = datasetSyncKey(id);
  for (;;) {
    const query = qc
      .getQueryCache()
      .find<DatasetCopy>({ queryKey, exact: true });
    if (!query) return;
    if (query.state.data && query.state.data.seq >= seq) return;
    const running =
      query.state.fetchStatus === "fetching" ? query.promise : undefined;
    if (!running) {
      await qc.invalidateQueries({ queryKey, exact: true });
      return;
    }
    // A refused sync is the screen's to show; this one only waits.
    if (
      !(await running.then(
        () => true,
        () => false,
      ))
    )
      return;
  }
}

function noticed(qc: QueryClient, payload: unknown, always: boolean): void {
  const p = payload as { datasetId?: unknown; seq?: unknown } | null;
  if (typeof p?.datasetId !== "string") return;
  // The own list shows each one's row count and last change (read only where shown).
  void qc.invalidateQueries({ queryKey: meDatasetListKey, exact: true });
  // Results worked out where the dataset is are asked again (only those shown).
  void qc.invalidateQueries({
    queryKey: ["me", "datasets", p.datasetId, "query"],
  });
  const queryKey = datasetSyncKey(p.datasetId);
  if (!qc.getQueryState(queryKey)) return;
  if (!always && typeof p.seq === "number") {
    void catchUpDataset(qc, p.datasetId, p.seq);
    return;
  }
  void qc.invalidateQueries({ queryKey, exact: true });
}

/** Me's events about datasets (registered by the shell part). */
export const datasetRealtime: RealtimeHandlers = {
  "dataset.advanced": (payload, { qc }) => noticed(qc, payload, false),
  "dataset.changed": (payload, { qc }) => noticed(qc, payload, true),
};
